import { recordSupportError, scrubSupport } from '../lib/support-context';
import { supabase } from '../lib/supabase';
/**
 * Frontend Error Reporter (TASK-052A AC-6)
 * Reports unhandled errors to Netlify relay → Slack + Supabase + Better Stack.
 *
 * NOTE (OPS-513): this path is deliberately NOT wired to Sentry. Every caller
 * here is an UNCAUGHT error — the `window.error` / `unhandledrejection` handlers
 * below, and `main.jsx`'s root `Sentry.ErrorBoundary` `onError` — all of which
 * Sentry ALREADY captures automatically (its default `GlobalHandlers`
 * integration owns window errors; `Sentry.ErrorBoundary` auto-captures the
 * crashes it catches). Adding a manual `captureException` here would file a
 * SECOND, differently-fingerprinted card for the same crash. Handled (caught &
 * swallowed) failures that Sentry can't see — subtree `ErrorBoundary` and the
 * activity-tracker flush path — route to Sentry via `lib/sentry-capture.js`
 * directly. This relay remains the Slack/Supabase side-channel only.
 */

import { logError } from '../lib/betterstack-logger';
import { isBrowserExtensionUrl } from '../lib/sentry-capture';

const RELAY_URL = '/.netlify/functions/error-report';

let recentErrors = new Map();
const BATCH_WINDOW = 60000; // 1 minute
const MAX_SAME = 3;

async function reportError(errorMessage, { pageUrl, userAction, stack, sentryId } = {}) {
  recordSupportError(errorMessage, { route: pageUrl, stack, sentry_id: sentryId });
  const key = errorMessage?.substring(0, 100);
  const now = Date.now();
  const recent = recentErrors.get(key);

  if (recent && (now - recent.first) < BATCH_WINDOW) {
    recent.count++;
    if (recent.count > MAX_SAME) return; // Stop flooding
  } else {
    recentErrors.set(key, { first: now, count: 1 });
  }

  // Clean old entries
  for (const [k, v] of recentErrors) {
    if (now - v.first > BATCH_WINDOW) recentErrors.delete(k);
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    fetch(RELAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
      body: JSON.stringify({
        error_message: scrubSupport(errorMessage, 500),
        page_url: scrubSupport(pageUrl || window.location.pathname,1000),
        user_action: scrubSupport(userAction,500),
        stack: scrubSupport(stack,2048)
      })
    }).catch((err) => {
      logError('error-reporter-relay-failed', { error: err?.message, originalMessage: errorMessage });
    });
  } catch (err) {
    logError('error-reporter-send-failed', { error: err?.message, originalMessage: errorMessage });
  }
}

export function initErrorReporter() {
  window.addEventListener('error', (event) => {
    // Skip script loading errors and ResizeObserver
    if (!event.message || event.message.includes('ResizeObserver')) return;
    // Skip errors thrown by a third-party browser extension (OPS-1017 / OPS-1021).
    // Sentry drops these via `denyUrls`, but that is a hook inside the Sentry client
    // and cannot reach this relay — a separate pipeline (Netlify fn → Supabase
    // `error_log` + Better Stack Logs) with its own listener. Rationale and scheme
    // list live on `isBrowserExtensionUrl`. Importing it does not wire this path to
    // Sentry: it is a pure predicate, no `captureException` (see file header).
    if (isBrowserExtensionUrl(event.filename)) return;
    reportError(event.message, {
      stack: event.error?.stack,
      pageUrl: window.location.pathname
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason?.message || String(event.reason);
    // Skip network errors on expected failures
    if (msg.includes('Failed to fetch') && window.navigator.onLine === false) return;
    reportError(msg, {
      stack: event.reason?.stack,
      pageUrl: window.location.pathname
    });
  });
}

export { reportError };
